import { useState, useEffect, useRef } from "react";
import { Mic, MicOff, Zap, Flame, Trophy, Shield, Users, User, Heart, Star, Crown, Lock, Check, X, ChevronRight, BookOpen, DollarSign, Target, Volume2, ThumbsUp, Bell, Award, Gift, Send, Share2, Coffee, UserPlus } from "lucide-react";

// ============================================
// BESPOKE × THE GROVE — DUOLINGO EDITION v2
// Snackpass-inspired social gifting + referrals
// ============================================

// Duolingo color system
const D = {
  feather: "#58CC02",
  featherDark: "#46A302",
  featherLight: "#7CDB36",
  macaw: "#1CB0F6",
  macawDark: "#1899D6",
  cardinal: "#FF4B4B",
  cardinalDark: "#EA2B2B",
  bee: "#FFC800",
  beeDark: "#E5B800",
  fox: "#FF9600",
  foxDark: "#E08600",
  lily: "#CE82FF",
  lilyDark: "#B866E6",
  snow: "#FFFFFF",
  polar: "#F7F7F7",
  arctic: "#E5E5E5",
  hare: "#AFAFAF",
  eel: "#4B4B4B",
  whale: "#131F24",
  bg: "#131F24",
};

// Duolingo-style 3D button
function DuoButton({ children, color = D.feather, shadow = D.featherDark, onClick, style = {}, small = false }) {
  return (
    <button onClick={onClick} style={{
      background: color, border: "none",
      borderBottom: `4px solid ${shadow}`,
      borderRadius: small ? 12 : 16,
      padding: small ? "8px 18px" : "14px 24px",
      color: D.snow, fontSize: small ? 14 : 17, fontWeight: 800,
      cursor: "pointer", textTransform: "uppercase", letterSpacing: 0.8,
      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
      width: "100%", fontFamily: "inherit", transition: "all 0.1s",
      ...style,
    }}>
      {children}
    </button>
  );
}

function DuoOutlineButton({ children, onClick, style = {} }) {
  return (
    <button onClick={onClick} style={{
      background: D.snow, border: `2px solid ${D.arctic}`,
      borderBottom: `4px solid ${D.arctic}`,
      borderRadius: 16, padding: "12px 24px",
      color: D.hare, fontSize: 15, fontWeight: 800,
      cursor: "pointer", textTransform: "uppercase", letterSpacing: 0.5,
      width: "100%", fontFamily: "inherit", ...style,
    }}>
      {children}
    </button>
  );
}

// XP / Gem / Streak bar (Duolingo top bar)
function TopBar({ xp = 1240, streak = 7, gems = 485, hearts = 4 }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "6px 16px", background: D.snow,
    }}>
      {[
        { icon: <Flame size={20} color={D.fox} fill={D.fox} />, val: streak, color: D.fox },
        { icon: <Zap size={20} color={D.bee} fill={D.bee} />, val: xp, color: D.bee },
        { icon: <span style={{ fontSize: 18 }}>💎</span>, val: gems, color: D.macaw },
        { icon: <Heart size={18} color={D.cardinal} fill={D.cardinal} />, val: hearts, color: D.cardinal },
      ].map((item, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {item.icon}
          <span style={{ fontSize: 14, fontWeight: 800, color: item.color }}>{item.val}</span>
        </div>
      ))}
    </div>
  );
}

// Privacy shield
function PrivacyPill() {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 6,
      background: "#E8F5E9", borderRadius: 12, padding: "8px 12px",
      margin: "0 16px",
    }}>
      <Shield size={14} color={D.feather} />
      <span style={{ fontSize: 11, color: D.eel, fontWeight: 600 }}>
        Management sees <span style={{ color: D.feather }}>metrics only</span> — never audio, transcripts, or your feed.
      </span>
    </div>
  );
}

// Mascot
function Mascot({ size = 60, expression = "happy", style = {} }) {
  const faces = {
    happy: { eyes: "😄", bg: D.feather },
    excited: { eyes: "🤩", bg: D.bee },
    thinking: { eyes: "🤔", bg: D.macaw },
    fire: { eyes: "🔥", bg: D.fox },
    proud: { eyes: "😎", bg: D.lily },
    wink: { eyes: "😉", bg: D.feather },
    gift: { eyes: "🎁", bg: D.lily },
    love: { eyes: "🥰", bg: D.cardinal },
  };
  const f = faces[expression] || faces.happy;
  return (
    <div style={{
      width: size, height: size, borderRadius: size / 2,
      background: f.bg, display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.55, boxShadow: `0 4px 0 ${f.bg}88`, ...style,
    }}>
      {f.eyes}
    </div>
  );
}

// CSS animations
const StyleInjector = () => {
  useEffect(() => {
    if (document.getElementById("duo-keyframes")) return;
    const style = document.createElement("style");
    style.id = "duo-keyframes";
    style.textContent = `
      @keyframes duo-pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.08); } }
      @keyframes duo-ring1 { 0% { transform: scale(1); opacity:0.5; } 100% { transform: scale(1.6); opacity:0; } }
      @keyframes duo-ring2 { 0% { transform: scale(1); opacity:0.3; } 100% { transform: scale(2); opacity:0; } }
      @keyframes duo-bounce { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
      @keyframes duo-glow { 0%,100% { box-shadow: 0 0 8px #58CC0266; } 50% { box-shadow: 0 0 24px #58CC02AA, 0 0 48px #58CC0244; } }
      @keyframes duo-confetti { 0% { transform: translateY(0) rotate(0); opacity:1; } 100% { transform: translateY(-30px) rotate(180deg); opacity:0; } }
      @keyframes duo-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
      @keyframes duo-shine { 0% { opacity: 0.6; } 50% { opacity: 1; } 100% { opacity: 0.6; } }
    `;
    document.head.appendChild(style);
  }, []);
  return null;
};

// ============================================
// GIFT MODAL — Snackpass-inspired social gifting
// ============================================
function GiftModal({ onClose, giftType = "xp", giftAmount = 15 }) {
  const [selected, setSelected] = useState(null);
  const [sent, setSent] = useState(false);
  const [giftTab, setGiftTab] = useState("xp"); // xp, reward

  const crewmates = [
    { name: "Mia", emoji: "🦊", streak: 12 },
    { name: "Dev", emoji: "🐻", streak: 5 },
    { name: "Sam", emoji: "🦁", streak: 8 },
    { name: "Ava", emoji: "🐰", streak: 3 },
  ];

  const rewards = [
    { id: "coffee", emoji: "☕", name: "Free Coffee", cost: 50, desc: "Redeemable at The Grove" },
    { id: "drink", emoji: "🍹", name: "Shift Drink", cost: 80, desc: "Any drink after shift" },
    { id: "streak", emoji: "🛡️", name: "Streak Shield", cost: 30, desc: "Protects a friend's streak" },
  ];

  if (sent) {
    return (
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: 200,
        background: D.snow, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", padding: 30,
      }}>
        <div style={{ animation: "duo-bounce 0.6s ease-out" }}>
          <Mascot size={90} expression="love" />
        </div>
        <div style={{ fontSize: 26, fontWeight: 900, color: D.whale, marginTop: 20 }}>Gift Sent!</div>
        <div style={{ fontSize: 14, color: D.hare, marginTop: 8, textAlign: "center" }}>
          {selected && `${selected.name} just got your gift! They'll send something back your way soon.`}
        </div>
        <div style={{
          marginTop: 16, background: "#F3E8FF", borderRadius: 16, padding: "14px 20px",
          border: `2px solid ${D.lily}`, display: "flex", alignItems: "center", gap: 10,
        }}>
          <span style={{ fontSize: 22 }}>🎁</span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: D.lilyDark }}>
              {giftTab === "xp" ? `+${giftAmount} XP sent` : "Reward sent!"}
            </div>
            <div style={{ fontSize: 11, color: D.hare }}>
              The more you gift, the more you get back
            </div>
          </div>
        </div>
        <div style={{
          marginTop: 12, background: "#FFF8E1", borderRadius: 12, padding: "10px 16px",
          border: `2px solid ${D.bee}`,
        }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: D.beeDark }}>
            🔄 You've gifted 8 times this week — +40 bonus XP earned from gifting!
          </span>
        </div>
        <div style={{ marginTop: 24, width: "100%" }}>
          <DuoButton onClick={onClose}>Nice!</DuoButton>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: 200,
      background: D.snow, display: "flex", flexDirection: "column",
    }}>
      {/* Header */}
      <div style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={onClose} style={{
          background: "none", border: "none", color: D.hare, fontSize: 18, cursor: "pointer",
        }}>✕</button>
        <div style={{ flex: 1, fontSize: 18, fontWeight: 900, color: D.whale }}>Send a Gift 🎁</div>
      </div>

      {/* Gift type tabs */}
      <div style={{ display: "flex", gap: 8, padding: "0 16px 12px" }}>
        {[
          { id: "xp", label: "⚡ XP & Gems" },
          { id: "reward", label: "🎁 Rewards" },
        ].map((t) => (
          <button key={t.id} onClick={() => setGiftTab(t.id)} style={{
            flex: 1, padding: "8px 0", borderRadius: 12, fontSize: 12, fontWeight: 800,
            background: giftTab === t.id ? D.lily : D.snow,
            color: giftTab === t.id ? D.snow : D.hare,
            border: giftTab === t.id ? "none" : `2px solid ${D.arctic}`,
            borderBottom: giftTab === t.id ? `3px solid ${D.lilyDark}` : `3px solid ${D.arctic}`,
            cursor: "pointer", textTransform: "uppercase", fontFamily: "inherit",
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Gift type selection (rewards tab) */}
      {giftTab === "reward" && (
        <div style={{ padding: "0 16px 12px" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: D.hare, marginBottom: 8 }}>Pick a reward to gift:</div>
          <div style={{ display: "flex", gap: 8 }}>
            {rewards.map((r) => (
              <button key={r.id} onClick={() => {}} style={{
                flex: 1, background: D.polar, borderRadius: 14, padding: "10px 4px",
                textAlign: "center", border: `2px solid ${D.arctic}`, borderBottom: `4px solid ${D.arctic}`,
                cursor: "pointer",
              }}>
                <div style={{ fontSize: 24 }}>{r.emoji}</div>
                <div style={{ fontSize: 10, fontWeight: 800, color: D.whale, marginTop: 2 }}>{r.name}</div>
                <div style={{ fontSize: 9, color: D.hare, marginTop: 1 }}>{r.cost} 💎</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {giftTab === "xp" && (
        <div style={{ padding: "0 16px 8px" }}>
          <div style={{
            background: "#FFF8E1", borderRadius: 14, padding: "10px 14px",
            border: `2px solid ${D.bee}`, display: "flex", alignItems: "center", gap: 10,
          }}>
            <Zap size={20} color={D.bee} fill={D.bee} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: D.beeDark }}>Gift {giftAmount} XP</div>
              <div style={{ fontSize: 11, color: D.hare }}>You earned it — share the love!</div>
            </div>
          </div>
        </div>
      )}

      {/* Snackpass-style insight */}
      <div style={{
        margin: "0 16px 12px", padding: "10px 14px", borderRadius: 12,
        background: "#F3E8FF", border: `2px solid ${D.lily}`,
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: D.lilyDark }}>
          💡 <span style={{ fontWeight: 800 }}>Gift loop:</span> Friends who gift each other earn 2x more XP over time
        </div>
      </div>

      {/* Crew list */}
      <div style={{ flex: 1, overflowY: "auto", padding: "0 16px" }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: D.whale, marginBottom: 10 }}>Send to a crewmate</div>
        {crewmates.map((c, i) => (
          <button key={i} onClick={() => setSelected(c)} style={{
            width: "100%", display: "flex", alignItems: "center", gap: 12,
            padding: 14, background: selected?.name === c.name ? "#F3E8FF" : D.polar,
            borderRadius: 16, marginBottom: 8, cursor: "pointer",
            border: selected?.name === c.name ? `2px solid ${D.lily}` : `2px solid ${D.arctic}`,
            borderBottom: selected?.name === c.name ? `4px solid ${D.lilyDark}` : `4px solid ${D.arctic}`,
          }}>
            <span style={{ fontSize: 32 }}>{c.emoji}</span>
            <div style={{ flex: 1, textAlign: "left" }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: D.whale }}>{c.name}</div>
              <div style={{ fontSize: 11, color: D.hare }}>
                <Flame size={12} color={D.fox} style={{ verticalAlign: "middle" }} /> {c.streak}-day streak
              </div>
            </div>
            {selected?.name === c.name && (
              <div style={{
                width: 28, height: 28, borderRadius: 14,
                background: D.lily, display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Check size={16} color={D.snow} />
              </div>
            )}
          </button>
        ))}

        {/* Recent gifts */}
        <div style={{ fontSize: 13, fontWeight: 800, color: D.whale, marginTop: 16, marginBottom: 8 }}>Recent gifts 🎁</div>
        {[
          { from: "Mia 🦊", to: "You", item: "+10 XP", time: "2h ago", color: D.bee },
          { from: "You", to: "Dev 🐻", item: "☕ Free Coffee", time: "Yesterday", color: D.lily },
          { from: "Sam 🦁", to: "Mia 🦊", item: "🛡️ Streak Shield", time: "2 days ago", color: D.macaw },
        ].map((g, i) => (
          <div key={i} style={{
            display: "flex", alignItems: "center", gap: 10, padding: "10px 0",
            borderBottom: i < 2 ? `1px solid ${D.arctic}` : "none",
          }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: D.eel }}>{g.from}</span>
            <span style={{ fontSize: 10, color: D.hare }}>→</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: D.eel }}>{g.to}</span>
            <span style={{
              fontSize: 10, fontWeight: 800, color: g.color,
              background: `${g.color}18`, padding: "2px 8px", borderRadius: 6, marginLeft: "auto",
            }}>{g.item}</span>
            <span style={{ fontSize: 10, color: D.hare }}>{g.time}</span>
          </div>
        ))}
      </div>

      {/* Send button */}
      <div style={{ padding: "12px 16px" }}>
        <DuoButton
          color={selected ? D.lily : D.arctic}
          shadow={selected ? D.lilyDark : D.hare}
          onClick={() => selected && setSent(true)}
        >
          <Gift size={18} /> {selected ? `Send to ${selected.name}` : "Pick a crewmate"}
        </DuoButton>
      </div>
    </div>
  );
}

// ============================================
// REFERRAL CARD — Two-tier referral system
// ============================================
function ReferralCard({ onExpand }) {
  const [expanded, setExpanded] = useState(false);

  if (!expanded) {
    return (
      <button onClick={() => setExpanded(true)} style={{
        width: "100%", background: `linear-gradient(135deg, ${D.lily}, ${D.macaw})`,
        borderRadius: 20, padding: 20, border: "none",
        borderBottom: `4px solid ${D.lilyDark}`, cursor: "pointer", textAlign: "left",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 50, height: 50, borderRadius: 25,
            background: "rgba(255,255,255,0.2)", display: "flex",
            alignItems: "center", justifyContent: "center", fontSize: 26,
          }}>
            🤝
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 900, color: D.snow }}>Invite a Server Friend</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.8)", marginTop: 2 }}>
              You both earn rewards — grow the crew!
            </div>
          </div>
          <ChevronRight size={22} color={D.snow} />
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <div style={{
            flex: 1, background: "rgba(255,255,255,0.15)", borderRadius: 10, padding: "8px 10px",
            textAlign: "center",
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.7)", textTransform: "uppercase" }}>Your referrals</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: D.snow }}>3</div>
          </div>
          <div style={{
            flex: 1, background: "rgba(255,255,255,0.15)", borderRadius: 10, padding: "8px 10px",
            textAlign: "center",
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.7)", textTransform: "uppercase" }}>XP earned</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: D.snow }}>+450</div>
          </div>
        </div>
      </button>
    );
  }

  return (
    <div style={{
      background: D.snow, borderRadius: 20, border: `2px solid ${D.arctic}`,
      borderBottom: `4px solid ${D.arctic}`, overflow: "hidden",
    }}>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${D.lily}, ${D.macaw})`,
        padding: "16px 16px 20px", textAlign: "center",
      }}>
        <button onClick={() => setExpanded(false)} style={{
          position: "absolute", right: 20, background: "none", border: "none",
          color: "rgba(255,255,255,0.7)", fontSize: 14, cursor: "pointer",
        }}>✕</button>
        <div style={{ fontSize: 30, marginBottom: 6 }}>🤝</div>
        <div style={{ fontSize: 18, fontWeight: 900, color: D.snow }}>Invite Friends, Earn Together</div>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.8)", marginTop: 4 }}>
          Every referral earns you <span style={{ fontWeight: 800 }}>both</span> bonus XP + gems
        </div>
      </div>

      {/* Two tiers */}
      <div style={{ padding: 16 }}>
        {/* Tier 1: Same restaurant */}
        <div style={{
          background: D.polar, borderRadius: 16, padding: 14, marginBottom: 10,
          border: `2px solid ${D.feather}`, borderBottom: `4px solid ${D.featherDark}`,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 16,
              background: D.feather, display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Users size={16} color={D.snow} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: D.whale }}>Same Restaurant</div>
              <div style={{ fontSize: 11, color: D.hare }}>Invite a coworker at The Grove</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{
              flex: 1, background: "#FFF8E1", borderRadius: 10, padding: "6px 10px",
              textAlign: "center", border: `1px solid ${D.bee}`,
            }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: D.beeDark }}>You get</div>
              <div style={{ fontSize: 13, fontWeight: 900, color: D.bee }}>+100 XP</div>
              <div style={{ fontSize: 10, color: D.hare }}>+ 50 💎</div>
            </div>
            <div style={{
              flex: 1, background: "#FFF8E1", borderRadius: 10, padding: "6px 10px",
              textAlign: "center", border: `1px solid ${D.bee}`,
            }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: D.beeDark }}>They get</div>
              <div style={{ fontSize: 13, fontWeight: 900, color: D.bee }}>+100 XP</div>
              <div style={{ fontSize: 10, color: D.hare }}>+ 50 💎</div>
            </div>
          </div>
        </div>

        {/* Tier 2: Cross-restaurant */}
        <div style={{
          background: D.polar, borderRadius: 16, padding: 14, marginBottom: 12,
          border: `2px solid ${D.lily}`, borderBottom: `4px solid ${D.lilyDark}`,
          position: "relative",
        }}>
          <div style={{
            position: "absolute", top: -8, right: 12,
            background: D.fox, borderRadius: 8, padding: "3px 10px",
            fontSize: 9, fontWeight: 800, color: D.snow, textTransform: "uppercase",
          }}>
            2X BONUS
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 16,
              background: D.lily, display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Share2 size={16} color={D.snow} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: D.whale }}>Other Restaurant</div>
              <div style={{ fontSize: 11, color: D.hare }}>Invite a server friend anywhere</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{
              flex: 1, background: "#F3E8FF", borderRadius: 10, padding: "6px 10px",
              textAlign: "center", border: `1px solid ${D.lily}`,
            }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: D.lilyDark }}>You get</div>
              <div style={{ fontSize: 13, fontWeight: 900, color: D.lily }}>+250 XP</div>
              <div style={{ fontSize: 10, color: D.hare }}>+ 100 💎 + 🏅</div>
            </div>
            <div style={{
              flex: 1, background: "#F3E8FF", borderRadius: 10, padding: "6px 10px",
              textAlign: "center", border: `1px solid ${D.lily}`,
            }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: D.lilyDark }}>They get</div>
              <div style={{ fontSize: 13, fontWeight: 900, color: D.lily }}>+250 XP</div>
              <div style={{ fontSize: 10, color: D.hare }}>+ 100 💎 + ☕</div>
            </div>
          </div>
        </div>

        {/* Share code */}
        <div style={{
          background: D.whale, borderRadius: 14, padding: "12px 16px",
          textAlign: "center", marginBottom: 10,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: D.hare, textTransform: "uppercase", marginBottom: 4 }}>Your invite code</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: D.feather, letterSpacing: 3 }}>TARA-GROVE</div>
        </div>

        <DuoButton color={D.lily} shadow={D.lilyDark}>
          <Share2 size={18} /> Share Invite Link
        </DuoButton>

        {/* Referral leaderboard */}
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: D.whale, marginBottom: 8 }}>Top Referrers at The Grove 🏆</div>
          {[
            { name: "Mia 🦊", refs: 7, badge: "🥇" },
            { name: "You 🐸", refs: 3, badge: "🥈" },
            { name: "Dev 🐻", refs: 2, badge: "🥉" },
          ].map((r, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "8px 0",
              borderBottom: i < 2 ? `1px solid ${D.arctic}` : "none",
            }}>
              <span style={{ fontSize: 18 }}>{r.badge}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: r.name.includes("You") ? D.feather : D.eel }}>{r.name}</span>
              <span style={{ fontSize: 12, color: D.hare, marginLeft: "auto" }}>{r.refs} referrals</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================
// IPHONE FRAME
// ============================================
function IPhoneFrame({ children, time = "9:41" }) {
  return (
    <div style={{
      width: 375, height: 812, background: D.snow, borderRadius: 44,
      overflow: "hidden", position: "relative",
      border: "3px solid #2A2A2A",
      boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
      fontFamily: "'Nunito', -apple-system, BlinkMacSystemFont, 'SF Pro Rounded', sans-serif",
    }}>
      <div style={{
        height: 54, display: "flex", alignItems: "flex-end", justifyContent: "space-between",
        padding: "0 28px 4px", position: "relative", zIndex: 100, background: D.snow,
      }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: D.whale }}>{time}</span>
        <div style={{
          position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)",
          width: 126, height: 34, background: "#000", borderRadius: "0 0 20px 20px",
        }} />
        <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
          <div style={{ width: 16, height: 10, display: "flex", gap: 1, alignItems: "flex-end" }}>
            {[4, 6, 8, 10].map((h, i) => (
              <div key={i} style={{ width: 3, height: h, background: D.whale, borderRadius: 1 }} />
            ))}
          </div>
          <div style={{ width: 25, height: 12, border: `2px solid ${D.whale}`, borderRadius: 3, padding: 1, position: "relative" }}>
            <div style={{ width: "75%", height: "100%", background: D.feather, borderRadius: 1 }} />
          </div>
        </div>
      </div>
      <div style={{ height: 812 - 54 - 34, overflow: "hidden", position: "relative" }}>
        {children}
      </div>
      <div style={{ height: 34, display: "flex", alignItems: "center", justifyContent: "center", background: D.snow }}>
        <div style={{ width: 134, height: 5, background: D.arctic, borderRadius: 3 }} />
      </div>
    </div>
  );
}

// ============================================
// TAB BAR
// ============================================
function TabBar({ active, onNav, isRecording, onToggleRecord }) {
  const tabs = [
    { id: "learn", icon: BookOpen, label: "Learn" },
    { id: "earnings", icon: DollarSign, label: "Earn" },
    { id: "record", isCenter: true },
    { id: "crew", icon: Users, label: "Crew" },
    { id: "profile", icon: User, label: "Profile" },
  ];
  return (
    <div style={{
      position: "absolute", bottom: 0, left: 0, right: 0, height: 72,
      background: D.snow, borderTop: `2px solid ${D.arctic}`,
      display: "flex", justifyContent: "space-around", alignItems: "flex-end", paddingBottom: 4,
    }}>
      {tabs.map((t) => {
        if (t.isCenter) {
          return (
            <button key="record" onClick={onToggleRecord} style={{
              background: "none", border: "none", cursor: "pointer",
              display: "flex", flexDirection: "column", alignItems: "center",
              padding: 0, marginTop: -24, position: "relative",
            }}>
              {isRecording && (
                <>
                  <div style={{
                    position: "absolute", top: -2, left: "50%", marginLeft: -32,
                    width: 64, height: 64, borderRadius: 32,
                    border: `3px solid ${D.feather}`,
                    animation: "duo-ring1 1.5s ease-out infinite",
                  }} />
                  <div style={{
                    position: "absolute", top: -6, left: "50%", marginLeft: -36,
                    width: 72, height: 72, borderRadius: 36,
                    border: `2px solid ${D.feather}`,
                    animation: "duo-ring2 1.5s ease-out infinite 0.2s",
                  }} />
                </>
              )}
              <div style={{
                width: 60, height: 60, borderRadius: 30,
                background: isRecording ? D.feather : D.snow,
                border: isRecording ? `3px solid ${D.featherDark}` : `3px solid ${D.arctic}`,
                borderBottom: isRecording ? `5px solid ${D.featherDark}` : `5px solid ${D.arctic}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                animation: isRecording ? "duo-pulse 1.5s ease-in-out infinite" : "none",
              }}>
                {isRecording
                  ? <Mic size={26} color={D.snow} strokeWidth={2.5} />
                  : <Mic size={26} color={D.hare} />
                }
              </div>
              <span style={{
                fontSize: 9, fontWeight: 800, marginTop: 2,
                color: isRecording ? D.feather : D.hare, textTransform: "uppercase",
              }}>
                {isRecording ? "● Live" : "Record"}
              </span>
            </button>
          );
        }
        const isActive = active === t.id;
        const Icon = t.icon;
        return (
          <button key={t.id} onClick={() => onNav(t.id)} style={{
            background: "none", border: "none", display: "flex", flexDirection: "column",
            alignItems: "center", gap: 2, cursor: "pointer", padding: "6px 12px",
          }}>
            <Icon size={22} color={isActive ? D.macaw : D.hare} strokeWidth={isActive ? 2.5 : 2} fill={isActive ? D.macaw : "none"} />
            <span style={{ fontSize: 10, fontWeight: 800, color: isActive ? D.macaw : D.hare }}>
              {t.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ============================================
// SCREEN: LEARN
// ============================================
function LearnScreen({ onStartDrill }) {
  const lessons = [
    { id: 1, title: "Upsell Basics", emoji: "🍹", color: D.feather, shadow: D.featherDark, done: true, xp: 40 },
    { id: 2, title: "Drink Pairings", emoji: "🍷", color: D.feather, shadow: D.featherDark, done: true, xp: 35 },
    { id: 3, title: "The Grove Burger", emoji: "🍔", color: D.bee, shadow: D.beeDark, done: true, xp: 30 },
    { id: 4, title: "Cookie Closer", emoji: "🍪", color: D.feather, shadow: D.featherDark, done: false, current: true, xp: 25 },
    { id: 5, title: "Pastrami Pitch", emoji: "🥪", color: D.arctic, shadow: D.hare, done: false, locked: true },
    { id: 6, title: "Reading the Table", emoji: "👀", color: D.arctic, shadow: D.hare, done: false, locked: true },
    { id: 7, title: "Wine Confidence", emoji: "🍷", color: D.arctic, shadow: D.hare, done: false, locked: true },
  ];

  return (
    <div style={{ height: "100%", overflowY: "auto", paddingBottom: 84, background: D.snow }}>
      <TopBar />

      <div style={{
        background: D.feather, padding: "16px 20px", margin: "0 16px", borderRadius: 16,
        display: "flex", alignItems: "center", gap: 14,
      }}>
        <Mascot size={50} expression="excited" style={{ background: D.snow, boxShadow: "none" }} />
        <div>
          <div style={{ fontSize: 11, fontWeight: 800, color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: 1 }}>Unit 1</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: D.snow }}>Counter Upselling</div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.8)", marginTop: 2 }}>3/7 lessons complete</div>
        </div>
      </div>

      {/* Learning path */}
      <div style={{ padding: "20px 0", display: "flex", flexDirection: "column", alignItems: "center" }}>
        {lessons.map((lesson, i) => {
          const offset = [0, 40, 20, -20, -40, -10, 30][i] || 0;
          return (
            <div key={lesson.id} style={{ marginBottom: 12, position: "relative", marginLeft: offset }}>
              {i < lessons.length - 1 && (
                <div style={{
                  position: "absolute", bottom: -14, left: "50%", transform: "translateX(-50%)",
                  width: 3, height: 14, background: D.arctic, borderRadius: 2,
                }} />
              )}
              <button onClick={lesson.current ? onStartDrill : undefined} style={{
                width: 70, height: 70, borderRadius: 35,
                background: lesson.done ? lesson.color : lesson.current ? lesson.color : D.polar,
                border: "none",
                borderBottom: `5px solid ${lesson.done ? lesson.shadow : lesson.current ? lesson.shadow : D.arctic}`,
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                cursor: lesson.locked ? "default" : "pointer", position: "relative",
                animation: lesson.current ? "duo-bounce 2s ease-in-out infinite" : "none",
                opacity: lesson.locked ? 0.5 : 1,
              }}>
                {lesson.locked ? (
                  <Lock size={22} color={D.hare} />
                ) : lesson.done ? (
                  <>
                    <span style={{ fontSize: 24 }}>{lesson.emoji}</span>
                    <Check size={14} color={D.snow} style={{ position: "absolute", bottom: -2, right: -2, background: D.feather, borderRadius: 10, padding: 1 }} />
                  </>
                ) : (
                  <span style={{ fontSize: 28 }}>{lesson.emoji}</span>
                )}
                {lesson.current && (
                  <div style={{
                    position: "absolute", top: -10, right: -10,
                    background: D.bee, borderRadius: 8, padding: "2px 6px",
                    fontSize: 9, fontWeight: 800, color: D.snow,
                  }}>
                    START
                  </div>
                )}
              </button>
              <div style={{
                textAlign: "center", marginTop: 4, fontSize: 11, fontWeight: 700,
                color: lesson.locked ? D.hare : lesson.current ? D.feather : D.eel,
              }}>
                {lesson.title}
              </div>
              {lesson.done && (
                <div style={{ textAlign: "center", fontSize: 10, color: D.hare, fontWeight: 600 }}>+{lesson.xp} XP</div>
              )}
            </div>
          );
        })}
      </div>

      <PrivacyPill />
    </div>
  );
}

// ============================================
// SCREEN: DRILL (with post-drill gifting)
// ============================================
function DrillScreen({ onBack, onOpenGift }) {
  const [phase, setPhase] = useState("intro");
  const [score, setScore] = useState(0);

  if (phase === "intro") {
    return (
      <div style={{ height: "100%", background: D.snow, display: "flex", flexDirection: "column" }}>
        <TopBar />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 30 }}>
          <Mascot size={80} expression="thinking" />
          <div style={{ fontSize: 22, fontWeight: 800, color: D.whale, marginTop: 20, textAlign: "center" }}>
            Cookie Closer Drill
          </div>
          <div style={{ fontSize: 14, color: D.hare, marginTop: 8, textAlign: "center", lineHeight: 1.6 }}>
            A guest just paid for their sandwich. Pitch the fresh-baked cookie before they walk away!
          </div>
          <div style={{
            display: "flex", alignItems: "center", gap: 8, marginTop: 16,
            background: "#FFF8E1", borderRadius: 12, padding: "10px 16px",
            border: `2px solid ${D.bee}`,
          }}>
            <Zap size={18} color={D.bee} fill={D.bee} />
            <span style={{ fontSize: 13, fontWeight: 800, color: D.beeDark }}>+25 XP</span>
            <span style={{ fontSize: 11, color: D.hare }}>• Score 80%+ for +15 bonus</span>
          </div>
        </div>
        <div style={{ padding: "0 20px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
          <DuoButton onClick={() => setPhase("recording")}>
            <Mic size={20} /> Start Drill
          </DuoButton>
          <DuoOutlineButton onClick={onBack}>Maybe Later</DuoOutlineButton>
        </div>
      </div>
    );
  }

  if (phase === "recording") {
    return (
      <div style={{ height: "100%", background: D.polar, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 30 }}>
        <div style={{ position: "relative", marginBottom: 24 }}>
          <svg width="160" height="160" viewBox="0 0 160 160">
            <circle cx="80" cy="80" r="72" fill="none" stroke={D.arctic} strokeWidth="8" />
            <circle cx="80" cy="80" r="72" fill="none" stroke={D.feather} strokeWidth="8"
              strokeDasharray={`${0.65 * 452} ${452}`} strokeLinecap="round"
              transform="rotate(-90 80 80)" />
          </svg>
          <div style={{
            position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
            textAlign: "center",
          }}>
            <div style={{ fontSize: 36, fontWeight: 900, color: D.whale }}>0:38</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: D.feather, textTransform: "uppercase" }}>Recording</div>
          </div>
        </div>
        <Mascot size={56} expression="happy" style={{ marginBottom: 16 }} />
        <div style={{ fontSize: 14, color: D.hare, textAlign: "center", maxWidth: 260 }}>
          "Pitch the fresh-baked cookie to a guest who just ordered a sandwich..."
        </div>
        <div style={{ marginTop: 30, width: "100%", padding: "0 20px" }}>
          <DuoButton color={D.cardinal} shadow={D.cardinalDark} onClick={() => { setScore(88); setPhase("result"); }}>
            Finish Early
          </DuoButton>
        </div>
      </div>
    );
  }

  // Result — with gift CTA
  return (
    <div style={{ height: "100%", background: D.snow, overflowY: "auto" }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "20px 30px" }}>
        <Mascot size={80} expression="proud" />
        <div style={{ fontSize: 26, fontWeight: 900, color: D.whale, marginTop: 16 }}>Great job!</div>
        <div style={{ fontSize: 14, color: D.hare, marginTop: 4 }}>You nailed the cookie pitch!</div>

        <div style={{
          width: 120, height: 120, borderRadius: 60,
          border: `6px solid ${D.feather}`, display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", marginTop: 20,
        }}>
          <div style={{ fontSize: 38, fontWeight: 900, color: D.feather }}>{score}%</div>
        </div>

        {/* XP breakdown */}
        <div style={{
          marginTop: 20, background: "#FFF8E1", borderRadius: 16, padding: 16,
          width: "100%", border: `2px solid ${D.bee}`,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: D.eel }}>Drill completed</span>
            <span style={{ fontSize: 14, fontWeight: 800, color: D.bee }}>+25 XP</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: D.eel }}>Score bonus (80%+)</span>
            <span style={{ fontSize: 14, fontWeight: 800, color: D.bee }}>+15 XP</span>
          </div>
          <div style={{ borderTop: `2px solid ${D.beeDark}33`, paddingTop: 8, display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontSize: 16, fontWeight: 800, color: D.whale }}>Total</span>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Zap size={18} color={D.bee} fill={D.bee} />
              <span style={{ fontSize: 18, fontWeight: 900, color: D.bee }}>+40 XP</span>
            </div>
          </div>
        </div>

        {/* Feedback */}
        <div style={{ marginTop: 14, width: "100%" }}>
          {[
            { label: "Enthusiasm", score: "Great", color: D.feather },
            { label: "Specificity", score: "Good", color: D.macaw },
            { label: "Closing ask", score: "Perfect", color: D.feather },
          ].map((f, i) => (
            <div key={i} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "8px 0", borderBottom: i < 2 ? `1px solid ${D.arctic}` : "none",
            }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: D.eel }}>{f.label}</span>
              <span style={{ fontSize: 13, fontWeight: 800, color: f.color, background: `${f.color}18`, padding: "3px 10px", borderRadius: 8 }}>{f.score}</span>
            </div>
          ))}
        </div>

        {/* 🎁 GIFT CTA — Snackpass-inspired */}
        <div style={{
          marginTop: 16, width: "100%", background: "#F3E8FF",
          borderRadius: 16, padding: 16, border: `2px solid ${D.lily}`,
          borderBottom: `4px solid ${D.lilyDark}`,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 24, animation: "duo-float 2s ease-in-out infinite" }}>🎁</span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: D.lilyDark }}>Gift XP to a friend!</div>
              <div style={{ fontSize: 11, color: D.hare }}>Share 15 XP with a crewmate — they'll send back too</div>
            </div>
          </div>
          <DuoButton color={D.lily} shadow={D.lilyDark} onClick={onOpenGift} small>
            <Gift size={16} /> Send a Gift
          </DuoButton>
        </div>
      </div>
      <div style={{ padding: "8px 20px 20px" }}>
        <DuoButton onClick={onBack}>Continue</DuoButton>
      </div>
    </div>
  );
}

// ============================================
// SCREEN: EARNINGS (with Rewards Shop)
// ============================================
function EarningsScreen({ isRecording }) {
  const [period, setPeriod] = useState("today");
  const [showShop, setShowShop] = useState(false);

  return (
    <div style={{ height: "100%", overflowY: "auto", paddingBottom: 84, background: D.snow }}>
      <TopBar />

      {isRecording && (
        <div style={{
          margin: "0 16px 8px", padding: "8px 14px", borderRadius: 12,
          background: "#E8F5E9", border: `2px solid ${D.feather}`,
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <div style={{
            width: 10, height: 10, borderRadius: 5, background: D.feather,
            animation: "duo-pulse 1.5s ease-in-out infinite",
          }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: D.feather }}>Shift recording in progress</span>
        </div>
      )}

      {/* Period tabs */}
      <div style={{ display: "flex", gap: 8, padding: "8px 16px" }}>
        {["today", "week", "month"].map((p) => (
          <button key={p} onClick={() => setPeriod(p)} style={{
            flex: 1, padding: "8px 0", borderRadius: 12, fontSize: 13, fontWeight: 800,
            background: period === p ? D.feather : D.snow,
            color: period === p ? D.snow : D.hare,
            border: period === p ? "none" : `2px solid ${D.arctic}`,
            borderBottom: period === p ? `3px solid ${D.featherDark}` : `3px solid ${D.arctic}`,
            cursor: "pointer", textTransform: "uppercase", fontFamily: "inherit",
          }}>
            {p === "today" ? "Today" : p === "week" ? "Week" : "Month"}
          </button>
        ))}
      </div>

      {/* Big money card */}
      <div style={{
        margin: "12px 16px", background: D.feather, borderRadius: 20, padding: 20,
        borderBottom: `5px solid ${D.featherDark}`, textAlign: "center",
      }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: 1 }}>
          {period === "today" ? "Tip lift today" : period === "week" ? "Tip lift this week" : "Tip lift this month"}
        </div>
        <div style={{ fontSize: 48, fontWeight: 900, color: D.snow, marginTop: 4 }}>
          {period === "today" ? "+$24" : period === "week" ? "+$147" : "+$583"}
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "rgba(255,255,255,0.8)", marginTop: 4 }}>
          {period === "today" ? "↑ 12% vs your average" : period === "week" ? "↑ 8% vs last week" : "↑ 15% vs last month"}
        </div>
        {period === "month" && (
          <div style={{
            marginTop: 10, background: "rgba(255,255,255,0.2)", borderRadius: 10,
            padding: "6px 12px", display: "inline-block",
          }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: D.snow }}>On pace for +$6,996 this year 🎉</span>
          </div>
        )}
      </div>

      {/* Stats grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, padding: "0 16px", marginBottom: 12 }}>
        {[
          { label: "Orders", value: "14", emoji: "🍽️" },
          { label: "Upsells", value: "71%", emoji: "📈" },
          { label: "Avg Check", value: "$42", emoji: "💰" },
        ].map((s, i) => (
          <div key={i} style={{
            background: D.polar, borderRadius: 14, padding: 12, textAlign: "center",
            border: `2px solid ${D.arctic}`,
          }}>
            <div style={{ fontSize: 20, marginBottom: 2 }}>{s.emoji}</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: D.whale }}>{s.value}</div>
            <div style={{ fontSize: 10, fontWeight: 700, color: D.hare, textTransform: "uppercase" }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* 🎁 REWARDS SHOP — Snackpass-inspired */}
      <div style={{ padding: "0 16px", marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: D.whale }}>Rewards Shop 🎁</div>
          <div style={{
            display: "flex", alignItems: "center", gap: 4,
            background: "#E3F2FD", borderRadius: 10, padding: "4px 10px",
          }}>
            <span style={{ fontSize: 14 }}>💎</span>
            <span style={{ fontSize: 13, fontWeight: 800, color: D.macaw }}>485</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
          {[
            { emoji: "☕", name: "Free Coffee", cost: 50, desc: "At The Grove", color: D.fox, shadow: D.foxDark },
            { emoji: "🍹", name: "Shift Drink", cost: 80, desc: "Any drink", color: D.lily, shadow: D.lilyDark },
            { emoji: "🍪", name: "Cookie Pack", cost: 30, desc: "3 cookies!", color: D.bee, shadow: D.beeDark },
            { emoji: "⭐", name: "Station Pick", cost: 150, desc: "Choose your spot", color: D.macaw, shadow: D.macawDark },
          ].map((r, i) => (
            <div key={i} style={{
              minWidth: 110, background: D.snow, borderRadius: 16, padding: "12px 10px",
              textAlign: "center", border: `2px solid ${r.color}30`,
              borderBottom: `4px solid ${r.shadow}40`, cursor: "pointer",
            }}>
              <div style={{ fontSize: 30, marginBottom: 4, animation: "duo-float 3s ease-in-out infinite" }}>{r.emoji}</div>
              <div style={{ fontSize: 12, fontWeight: 800, color: D.whale }}>{r.name}</div>
              <div style={{ fontSize: 10, color: D.hare, marginTop: 1 }}>{r.desc}</div>
              <div style={{
                marginTop: 6, display: "inline-flex", alignItems: "center", gap: 4,
                background: `${r.color}18`, borderRadius: 8, padding: "3px 10px",
              }}>
                <span style={{ fontSize: 11 }}>💎</span>
                <span style={{ fontSize: 12, fontWeight: 800, color: r.color }}>{r.cost}</span>
              </div>
              <div style={{
                marginTop: 6, fontSize: 9, fontWeight: 700, color: D.lily,
                cursor: "pointer",
              }}>
                or gift to friend →
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* What's Working */}
      <div style={{ padding: "0 16px" }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: D.whale, marginBottom: 10 }}>What's Working 💡</div>
        {[
          { emoji: "🍹", text: "Cocktail upsells are your move", detail: "+$9 avg per suggestion", color: D.feather },
          { emoji: "🍔", text: "Burger add-ons climbing", detail: "44% → 61% this month!", color: D.fox },
          { emoji: "🍪", text: "Try today", detail: "Mention the fresh-baked cookie at checkout", color: D.macaw },
        ].map((tip, i) => (
          <div key={i} style={{
            display: "flex", alignItems: "center", gap: 12, padding: 14,
            background: D.polar, borderRadius: 14, marginBottom: 8,
            border: `2px solid ${D.arctic}`,
          }}>
            <div style={{ fontSize: 28 }}>{tip.emoji}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: D.whale }}>{tip.text}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: tip.color, marginTop: 2 }}>{tip.detail}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// SCREEN: SHIFT (with post-shift gift CTA)
// ============================================
function ShiftScreen({ isRecording, seconds, onEnd, onOpenGift }) {
  const formatTime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  if (seconds >= 999) {
    // Post-shift scorecard with gift CTA
    return (
      <div style={{ height: "100%", overflowY: "auto", paddingBottom: 84, background: D.snow }}>
        <TopBar />
        <div style={{ textAlign: "center", padding: "10px 20px" }}>
          <Mascot size={70} expression="proud" />
          <div style={{ fontSize: 24, fontWeight: 900, color: D.whale, marginTop: 10 }}>Shift Complete!</div>
          <div style={{ fontSize: 13, color: D.hare }}>Tuesday lunch • 11:04 AM — 3:52 PM</div>
        </div>

        {/* XP earned */}
        <div style={{
          margin: "10px 16px", background: "#FFF8E1", borderRadius: 16, padding: 16,
          border: `2px solid ${D.bee}`, textAlign: "center",
        }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: D.hare, textTransform: "uppercase" }}>XP Earned</div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 4 }}>
            <Zap size={28} color={D.bee} fill={D.bee} />
            <span style={{ fontSize: 36, fontWeight: 900, color: D.bee }}>+85</span>
          </div>
          <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 8 }}>
            {[
              { label: "Shift", val: "+40" },
              { label: "Streak", val: "+20" },
              { label: "Drill", val: "+25" },
            ].map((x, i) => (
              <div key={i} style={{ fontSize: 11, color: D.hare }}>
                {x.label} <span style={{ fontWeight: 800, color: D.beeDark }}>{x.val}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Tip lift */}
        <div style={{
          margin: "10px 16px", background: D.feather, borderRadius: 16, padding: 20,
          borderBottom: `4px solid ${D.featherDark}`, textAlign: "center",
        }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: "rgba(255,255,255,0.7)", textTransform: "uppercase" }}>Estimated Tip Lift</div>
          <div style={{ fontSize: 48, fontWeight: 900, color: D.snow }}>+$27</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.8)" }}>Your best Tuesday lunch in 3 weeks 🎉</div>
        </div>

        {/* 🎁 POST-SHIFT GIFT — Snackpass-inspired social loop */}
        <div style={{
          margin: "10px 16px", background: `linear-gradient(135deg, ${D.lily}18, ${D.macaw}18)`,
          borderRadius: 18, padding: 16,
          border: `2px solid ${D.lily}`, borderBottom: `4px solid ${D.lilyDark}`,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 22,
              background: D.lily, display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 22, animation: "duo-bounce 2s ease-in-out infinite",
            }}>
              🎁
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 900, color: D.whale }}>Send a Shift Gift!</div>
              <div style={{ fontSize: 12, color: D.hare }}>
                Great shift? Share some love with the crew
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            {[
              { emoji: "⚡", label: "+20 XP", desc: "Gift XP" },
              { emoji: "☕", label: "Coffee", desc: "50 💎" },
              { emoji: "🛡️", label: "Shield", desc: "30 💎" },
            ].map((g, i) => (
              <div key={i} style={{
                flex: 1, background: D.snow, borderRadius: 12, padding: "8px 4px",
                textAlign: "center", border: `1px solid ${D.arctic}`,
              }}>
                <div style={{ fontSize: 18 }}>{g.emoji}</div>
                <div style={{ fontSize: 10, fontWeight: 800, color: D.whale }}>{g.label}</div>
                <div style={{ fontSize: 9, color: D.hare }}>{g.desc}</div>
              </div>
            ))}
          </div>
          <DuoButton color={D.lily} shadow={D.lilyDark} onClick={onOpenGift} small>
            <Gift size={16} /> Pick a Gift to Send
          </DuoButton>
        </div>

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, padding: "8px 16px" }}>
          {[
            { label: "Orders", value: "22", emoji: "🍽️" },
            { label: "Drink Upsells", value: "68%", emoji: "🍹" },
            { label: "Revenue", value: "$847", emoji: "💰" },
            { label: "Check Lift", value: "+$6", emoji: "⬆️" },
          ].map((s, i) => (
            <div key={i} style={{
              background: D.polar, borderRadius: 14, padding: 14, textAlign: "center",
              border: `2px solid ${D.arctic}`,
            }}>
              <div style={{ fontSize: 22 }}>{s.emoji}</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: D.whale }}>{s.value}</div>
              <div style={{ fontSize: 10, fontWeight: 700, color: D.hare, textTransform: "uppercase" }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Coach note */}
        <div style={{
          margin: "10px 16px", background: D.polar, borderRadius: 14, padding: 14,
          border: `2px solid ${D.arctic}`, display: "flex", gap: 10,
        }}>
          <Mascot size={40} expression="wink" />
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, color: D.whale }}>Coach's Note</div>
            <div style={{ fontSize: 12, color: D.hare, marginTop: 2, lineHeight: 1.5 }}>
              You crushed drink upsells! Try mentioning the cookie at checkout — servers who do add +$4 per check. 🍪
            </div>
          </div>
        </div>

        <div style={{ margin: "8px 16px" }}>
          <PrivacyPill />
        </div>
      </div>
    );
  }

  // Active recording
  return (
    <div style={{ height: "100%", background: D.polar, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", paddingBottom: 84 }}>
      <div style={{ position: "relative", marginBottom: 24 }}>
        <svg width="180" height="180" viewBox="0 0 180 180">
          <circle cx="90" cy="90" r="80" fill="none" stroke={D.arctic} strokeWidth="10" />
          <circle cx="90" cy="90" r="80" fill="none" stroke={D.feather} strokeWidth="10"
            strokeDasharray={`${Math.min(seconds / 18000, 1) * 502} ${502}`} strokeLinecap="round"
            transform="rotate(-90 90 90)" />
        </svg>
        <div style={{
          position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
          textAlign: "center",
        }}>
          <Mascot size={44} expression={isRecording ? "happy" : "thinking"} style={{ margin: "0 auto 4px" }} />
          <div style={{ fontSize: 11, fontWeight: 800, color: isRecording ? D.feather : D.hare, textTransform: "uppercase" }}>
            {isRecording ? "● Recording" : "Paused"}
          </div>
        </div>
      </div>

      <div style={{ fontSize: 44, fontWeight: 900, color: D.whale, fontVariantNumeric: "tabular-nums" }}>
        {formatTime(seconds)}
      </div>

      <div style={{ display: "flex", gap: 24, marginTop: 24 }}>
        {[
          { label: "Orders", value: "14", emoji: "🍽️" },
          { label: "Upsells", value: "10", emoji: "🍹" },
          { label: "Lift", value: "+$22", emoji: "💰" },
        ].map((s, i) => (
          <div key={i} style={{ textAlign: "center" }}>
            <div style={{ fontSize: 16 }}>{s.emoji}</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: D.whale }}>{s.value}</div>
            <div style={{ fontSize: 10, fontWeight: 700, color: D.hare, textTransform: "uppercase" }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{
        marginTop: 20, display: "flex", alignItems: "center", gap: 8,
        background: "#FFF8E1", borderRadius: 12, padding: "8px 16px",
        border: `2px solid ${D.bee}`,
      }}>
        <Zap size={16} color={D.bee} fill={D.bee} />
        <span style={{ fontSize: 13, fontWeight: 800, color: D.beeDark }}>+60 XP earned</span>
      </div>

      <div style={{ marginTop: 20, width: "calc(100% - 80px)" }}>
        <DuoButton color={D.cardinal} shadow={D.cardinalDark} onClick={onEnd} small>
          End Shift
        </DuoButton>
      </div>
    </div>
  );
}

// ============================================
// SCREEN: CREW (with Gifts tab)
// ============================================
function CrewScreen({ isRecording, onOpenGift }) {
  const [tab, setTab] = useState("feed");
  return (
    <div style={{ height: "100%", overflowY: "auto", paddingBottom: 84, background: D.snow }}>
      <TopBar />

      {isRecording && (
        <div style={{
          margin: "0 16px 8px", padding: "8px 14px", borderRadius: 12,
          background: "#E8F5E9", border: `2px solid ${D.feather}`,
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <div style={{ width: 10, height: 10, borderRadius: 5, background: D.feather, animation: "duo-pulse 1.5s ease-in-out infinite" }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: D.feather }}>Recording in progress</span>
        </div>
      )}

      <div style={{ padding: "0 16px 8px" }}>
        <div style={{ fontSize: 20, fontWeight: 900, color: D.whale }}>The Grove Crew 🌿</div>
      </div>

      {/* Tabs — now with Gifts */}
      <div style={{ display: "flex", gap: 6, padding: "0 16px 12px" }}>
        {["feed", "gifts", "battles", "challenges"].map((t) => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: "7px 12px", borderRadius: 12, fontSize: 12, fontWeight: 800,
            background: tab === t ? (t === "gifts" ? D.lily : D.macaw) : D.snow,
            color: tab === t ? D.snow : D.hare,
            border: tab === t ? "none" : `2px solid ${D.arctic}`,
            borderBottom: tab === t ? `3px solid ${t === "gifts" ? D.lilyDark : D.macawDark}` : `3px solid ${D.arctic}`,
            cursor: "pointer", textTransform: "uppercase", fontFamily: "inherit",
          }}>
            {t === "feed" ? "Feed" : t === "gifts" ? "🎁 Gifts" : t === "battles" ? "Battles" : "Quests"}
          </button>
        ))}
      </div>

      {tab === "feed" && (
        <div style={{ padding: "0 16px" }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 6, padding: "6px 10px",
            background: "#E8F5E9", borderRadius: 10, marginBottom: 10,
          }}>
            <Lock size={12} color={D.feather} />
            <span style={{ fontSize: 11, fontWeight: 600, color: D.eel }}>
              Crew-only — <span style={{ color: D.feather }}>management can't see this</span>
            </span>
          </div>

          {/* Gift received notification in feed */}
          <div style={{
            background: "#F3E8FF", borderRadius: 16, padding: 14, marginBottom: 8,
            border: `2px solid ${D.lily}`,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 22 }}>🎁</span>
              <span style={{ fontSize: 14, fontWeight: 800, color: D.lilyDark }}>Gift Alert!</span>
              <span style={{ fontSize: 11, color: D.hare, marginLeft: "auto" }}>Just now</span>
            </div>
            <div style={{ fontSize: 13, color: D.eel, lineHeight: 1.5, marginLeft: 30 }}>
              Mia 🦊 sent you <span style={{ fontWeight: 800, color: D.bee }}>+10 XP</span>! Send something back?
            </div>
            <button onClick={onOpenGift} style={{
              marginTop: 8, marginLeft: 30, background: D.lily, border: "none",
              borderBottom: `3px solid ${D.lilyDark}`, borderRadius: 10,
              padding: "6px 14px", color: D.snow, fontSize: 11, fontWeight: 800,
              cursor: "pointer", textTransform: "uppercase",
            }}>
              🎁 Gift Back
            </button>
          </div>

          {[
            { name: "Mia", emoji: "🦊", time: "1:42 PM", text: "20-order drink streak 🔥🔥🔥 counter was INSANE today", xp: 15 },
            { name: "Dev", emoji: "🐻", time: "1:15 PM", text: "Moved 6 Grove Burgers with bacon. That pitch works lol", xp: 10 },
            { name: "You", emoji: "🐸", time: "12:50 PM", text: "Cocktail career high — 15 out of 22 orders!", isYou: true, xp: 20 },
            { name: "Sam", emoji: "🦁", time: "12:20 PM", text: "$12 tip on a $19 sandwich after describing the pastrami 😂", xp: 10 },
          ].map((msg, i) => (
            <div key={i} style={{
              background: msg.isYou ? "#E8F5E9" : D.polar,
              borderRadius: 16, padding: 14, marginBottom: 8,
              border: msg.isYou ? `2px solid ${D.feather}` : `2px solid ${D.arctic}`,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 22 }}>{msg.emoji}</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: msg.isYou ? D.feather : D.whale }}>{msg.name}</span>
                {msg.xp > 0 && (
                  <span style={{
                    fontSize: 10, fontWeight: 800, color: D.bee,
                    background: "#FFF8E1", padding: "2px 8px", borderRadius: 8,
                  }}>+{msg.xp} XP</span>
                )}
                <span style={{ fontSize: 11, color: D.hare, marginLeft: "auto" }}>{msg.time}</span>
              </div>
              <div style={{ fontSize: 13, color: D.eel, lineHeight: 1.5, marginLeft: 30 }}>{msg.text}</div>
            </div>
          ))}
        </div>
      )}

      {/* 🎁 GIFTS TAB — Snackpass-inspired */}
      {tab === "gifts" && (
        <div style={{ padding: "0 16px" }}>
          {/* Gift loop stats */}
          <div style={{
            background: `linear-gradient(135deg, ${D.lily}, ${D.macaw})`,
            borderRadius: 20, padding: 20, marginBottom: 12,
            borderBottom: `4px solid ${D.lilyDark}`, textAlign: "center",
          }}>
            <div style={{ fontSize: 36, marginBottom: 4 }}>🎁</div>
            <div style={{ fontSize: 18, fontWeight: 900, color: D.snow }}>Gift Loop</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.8)", marginTop: 4 }}>
              Gift friends, get gifts back — everyone levels up faster!
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              {[
                { label: "Sent", val: "12", sub: "this week" },
                { label: "Received", val: "8", sub: "this week" },
                { label: "Bonus XP", val: "+60", sub: "from gifting" },
              ].map((s, i) => (
                <div key={i} style={{
                  flex: 1, background: "rgba(255,255,255,0.15)", borderRadius: 12, padding: "8px 4px",
                }}>
                  <div style={{ fontSize: 20, fontWeight: 900, color: D.snow }}>{s.val}</div>
                  <div style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.7)" }}>{s.label}</div>
                  <div style={{ fontSize: 8, color: "rgba(255,255,255,0.5)" }}>{s.sub}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Send a gift CTA */}
          <DuoButton color={D.lily} shadow={D.lilyDark} onClick={onOpenGift} style={{ marginBottom: 12 }}>
            <Gift size={18} /> Send a Gift Now
          </DuoButton>

          {/* Gift activity feed */}
          <div style={{ fontSize: 14, fontWeight: 800, color: D.whale, marginBottom: 10 }}>Recent Gift Activity</div>
          {[
            { from: "🦊 Mia", to: "🐸 You", item: "+10 XP", time: "20 min ago", color: D.bee, emoji: "⚡" },
            { from: "🐸 You", to: "🐻 Dev", item: "☕ Free Coffee", time: "2 hrs ago", color: D.fox, emoji: "☕" },
            { from: "🦁 Sam", to: "🦊 Mia", item: "🛡️ Streak Shield", time: "Yesterday", color: D.macaw, emoji: "🛡️" },
            { from: "🐻 Dev", to: "🐸 You", item: "+15 XP", time: "Yesterday", color: D.bee, emoji: "⚡" },
            { from: "🐸 You", to: "🦁 Sam", item: "+10 XP", time: "2 days ago", color: D.bee, emoji: "⚡" },
            { from: "🦊 Mia", to: "🐻 Dev", item: "🍹 Shift Drink", time: "3 days ago", color: D.lily, emoji: "🍹" },
          ].map((g, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 8, padding: "10px 12px",
              background: g.to.includes("You") ? "#F3E8FF" : D.polar,
              borderRadius: 14, marginBottom: 6,
              border: g.to.includes("You") ? `2px solid ${D.lily}40` : `2px solid ${D.arctic}`,
            }}>
              <span style={{ fontSize: 20 }}>{g.emoji}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: D.eel }}>
                  {g.from} → {g.to}
                </div>
                <div style={{ fontSize: 11, color: D.hare }}>{g.time}</div>
              </div>
              <span style={{
                fontSize: 11, fontWeight: 800, color: g.color,
                background: `${g.color}18`, padding: "3px 10px", borderRadius: 8,
              }}>{g.item}</span>
            </div>
          ))}

          {/* Gift streak */}
          <div style={{
            marginTop: 10, background: "#FFF8E1", borderRadius: 14, padding: 14,
            border: `2px solid ${D.bee}`, display: "flex", alignItems: "center", gap: 10,
          }}>
            <Flame size={22} color={D.fox} fill={D.fox} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: D.whale }}>Gift Streak: 5 days 🔥</div>
              <div style={{ fontSize: 11, color: D.hare }}>Gift every day for bonus XP! 2 more days = +50 bonus</div>
            </div>
          </div>
        </div>
      )}

      {tab === "battles" && (
        <div style={{ padding: "0 16px" }}>
          <div style={{
            background: D.fox, borderRadius: 20, padding: 20,
            borderBottom: `4px solid ${D.foxDark}`, textAlign: "center", marginBottom: 12,
          }}>
            <Trophy size={28} color={D.snow} style={{ margin: "0 auto 8px" }} />
            <div style={{ fontSize: 12, fontWeight: 800, color: "rgba(255,255,255,0.7)", textTransform: "uppercase" }}>Active Battle</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: D.snow, marginTop: 4 }}>Lunch Crew vs Dinner Crew</div>
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 16, marginTop: 12 }}>
              <div>
                <div style={{ fontSize: 28, fontWeight: 900, color: D.snow }}>$1,847</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.8)" }}>Lunch (You)</div>
              </div>
              <div style={{ fontSize: 16, fontWeight: 900, color: "rgba(255,255,255,0.5)" }}>vs</div>
              <div>
                <div style={{ fontSize: 28, fontWeight: 900, color: "rgba(255,255,255,0.6)" }}>$1,620</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>Dinner</div>
              </div>
            </div>
            <div style={{ background: "rgba(255,255,255,0.2)", borderRadius: 6, height: 10, marginTop: 12, overflow: "hidden" }}>
              <div style={{ width: "54%", height: "100%", background: D.snow, borderRadius: 6 }} />
            </div>
            <div style={{ fontSize: 12, fontWeight: 800, color: D.snow, marginTop: 6 }}>Leading by $227! Winners get +100 XP 🏆</div>
          </div>
        </div>
      )}

      {tab === "challenges" && (
        <div style={{ padding: "0 16px" }}>
          {[
            { name: "🍹 The Bartender", desc: "Upsell drinks on 5 orders", prog: 4, total: 5, color: D.lily, shadow: D.lilyDark, reward: "+50 XP + First Cut", hot: true },
            { name: "🍪 Cookie Monster", desc: "Sell 15 cookies this week", prog: 11, total: 15, color: D.bee, shadow: D.beeDark, reward: "+40 XP + Free meal" },
            { name: "🍔 Grove Special", desc: "Move 10 burgers with add-ons", prog: 6, total: 10, color: D.feather, shadow: D.featherDark, reward: "+60 XP + Station pick" },
            { name: "🤝 Crew Builder", desc: "Refer 3 server friends", prog: 1, total: 3, color: D.lily, shadow: D.lilyDark, reward: "+250 XP + 🏅 Badge" },
          ].map((ch, i) => (
            <div key={i} style={{
              background: D.snow, borderRadius: 16, padding: 16, marginBottom: 10,
              border: `2px solid ${ch.hot ? ch.color : D.arctic}`,
              borderBottom: `4px solid ${ch.hot ? ch.shadow : D.arctic}`,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontSize: 15, fontWeight: 800, color: D.whale }}>{ch.name}</span>
                {ch.hot && (
                  <span style={{ fontSize: 10, fontWeight: 800, color: D.snow, background: ch.color, padding: "3px 8px", borderRadius: 8 }}>ALMOST!</span>
                )}
              </div>
              <div style={{ fontSize: 12, color: D.hare, marginBottom: 8 }}>{ch.desc}</div>
              <div style={{ background: D.arctic, borderRadius: 6, height: 10, overflow: "hidden", marginBottom: 6 }}>
                <div style={{ width: `${(ch.prog / ch.total) * 100}%`, height: "100%", background: ch.color, borderRadius: 6 }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: ch.color }}>{ch.prog}/{ch.total}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: D.hare }}>{ch.reward}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// SCREEN: PROFILE (with Referrals)
// ============================================
function ProfileScreen({ isRecording }) {
  return (
    <div style={{ height: "100%", overflowY: "auto", paddingBottom: 84, background: D.snow }}>
      <TopBar />

      <div style={{ textAlign: "center", padding: "10px 20px 16px" }}>
        <div style={{
          width: 80, height: 80, borderRadius: 40,
          background: D.feather, display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 10px", fontSize: 40, borderBottom: `4px solid ${D.featherDark}`,
        }}>
          🐸
        </div>
        <div style={{ fontSize: 22, fontWeight: 900, color: D.whale }}>Tara K.</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: D.hare }}>The Grove SF • Counter + Table</div>

        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8, marginTop: 10,
          background: "#FFF8E1", borderRadius: 14, padding: "8px 16px",
          border: `2px solid ${D.bee}`,
        }}>
          <Zap size={18} color={D.bee} fill={D.bee} />
          <span style={{ fontSize: 15, fontWeight: 900, color: D.beeDark }}>Level 8</span>
          <span style={{ fontSize: 12, color: D.hare }}>1240 XP</span>
        </div>
      </div>

      {isRecording && (
        <div style={{
          margin: "0 16px 10px", padding: "8px 14px", borderRadius: 12,
          background: "#E8F5E9", border: `2px solid ${D.feather}`,
          display: "flex", alignItems: "center", gap: 8, justifyContent: "center",
        }}>
          <div style={{ width: 10, height: 10, borderRadius: 5, background: D.feather, animation: "duo-pulse 1.5s ease-in-out infinite" }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: D.feather }}>Recording in progress</span>
        </div>
      )}

      {/* XP progress */}
      <div style={{
        margin: "0 16px 12px", background: D.polar, borderRadius: 16, padding: 14,
        border: `2px solid ${D.arctic}`,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 800, color: D.whale }}>Level 8 → 9</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: D.hare }}>1240 / 1500</span>
        </div>
        <div style={{ background: D.arctic, borderRadius: 6, height: 12, overflow: "hidden" }}>
          <div style={{ width: "83%", height: "100%", background: `linear-gradient(90deg, ${D.bee}, ${D.fox})`, borderRadius: 6 }} />
        </div>
        <div style={{ fontSize: 11, color: D.hare, marginTop: 4 }}>260 XP to next level!</div>
      </div>

      {/* 🤝 REFERRAL CARD — Two-tier system */}
      <div style={{ padding: "0 16px", marginBottom: 12 }}>
        <ReferralCard />
      </div>

      {/* Bespoke Score */}
      <div style={{
        margin: "0 16px 12px", background: D.macaw, borderRadius: 20, padding: 20,
        borderBottom: `4px solid ${D.macawDark}`, textAlign: "center",
      }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: 1 }}>
          Bespoke Score
        </div>
        <div style={{
          width: 90, height: 90, borderRadius: 45,
          border: `5px solid ${D.snow}`, display: "flex", alignItems: "center", justifyContent: "center",
          margin: "12px auto",
        }}>
          <div style={{ fontSize: 34, fontWeight: 900, color: D.snow }}>87</div>
        </div>
        <div style={{ fontSize: 14, fontWeight: 800, color: D.snow }}>Top 15% of all servers</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginTop: 14 }}>
          {[
            { label: "Upsell", score: 92 },
            { label: "Hospitality", score: 84 },
            { label: "Consistency", score: 88 },
            { label: "Peer", score: 82 },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 900, color: D.snow }}>{s.score}</div>
              <div style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.7)" }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Badges — now includes gifting & referral badges */}
      <div style={{ padding: "0 16px", marginBottom: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 900, color: D.whale, marginBottom: 10 }}>Badges</div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {[
            { emoji: "🍹", name: "Drink Expert", color: D.feather },
            { emoji: "🔥", name: "Streak Master", color: D.fox },
            { emoji: "🍪", name: "Cookie Monster", color: D.bee },
            { emoji: "⚡", name: "Counter Pro", color: D.macaw },
            { emoji: "🎁", name: "Generous", color: D.lily },
            { emoji: "🤝", name: "Crew Builder", color: D.lily },
          ].map((b, i) => (
            <div key={i} style={{
              width: 90, background: D.polar, borderRadius: 14, padding: "10px 8px",
              textAlign: "center", border: `2px solid ${D.arctic}`, borderBottom: `4px solid ${D.arctic}`,
            }}>
              <div style={{ fontSize: 26, marginBottom: 2 }}>{b.emoji}</div>
              <div style={{ fontSize: 10, fontWeight: 800, color: D.whale }}>{b.name}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Career stats — includes gifting */}
      <div style={{ padding: "0 16px", marginBottom: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 900, color: D.whale, marginBottom: 10 }}>Stats</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {[
            { label: "Revenue", value: "$42.8K", emoji: "💰" },
            { label: "Shifts", value: "186", emoji: "📋" },
            { label: "Upsell Avg", value: "71%", emoji: "📈" },
            { label: "Tip Lift", value: "+$4,290", emoji: "🤑" },
            { label: "Gifts Sent", value: "47", emoji: "🎁" },
            { label: "Referrals", value: "3", emoji: "🤝" },
          ].map((s, i) => (
            <div key={i} style={{
              background: D.polar, borderRadius: 14, padding: 14, textAlign: "center",
              border: `2px solid ${D.arctic}`,
            }}>
              <div style={{ fontSize: 22 }}>{s.emoji}</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: D.whale }}>{s.value}</div>
              <div style={{ fontSize: 10, fontWeight: 700, color: D.hare, textTransform: "uppercase" }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Privacy */}
      <div style={{ padding: "0 16px 8px" }}>
        <div style={{
          background: "#E8F5E9", borderRadius: 14, padding: 14,
          border: `2px solid ${D.feather}`,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <Shield size={16} color={D.feather} />
            <span style={{ fontSize: 13, fontWeight: 800, color: D.feather }}>Data Firewall™</span>
          </div>
          <div style={{ fontSize: 12, color: D.eel, lineHeight: 1.5 }}>
            Your profile is yours. Management sees revenue metrics only — never audio, transcripts, or the crew feed.
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// SCREEN: ONBOARDING (now includes gifting intro)
// ============================================
function OnboardingScreen({ onDone }) {
  const [step, setStep] = useState(0);
  const steps = [
    <div key={0} style={{ textAlign: "center", padding: "40px 28px" }}>
      <Mascot size={90} expression="excited" />
      <div style={{ fontSize: 26, fontWeight: 900, color: D.whale, marginTop: 20, lineHeight: 1.2 }}>
        Make more money.<br />Every. Single. Shift.
      </div>
      <div style={{ fontSize: 14, color: D.hare, marginTop: 12, lineHeight: 1.6 }}>
        Bespoke helps you earn <span style={{ color: D.feather, fontWeight: 800 }}>+$400/month</span> in extra tips by coaching your upselling game.
      </div>
      <div style={{
        marginTop: 20, background: "#FFF8E1", borderRadius: 14, padding: "12px 16px",
        border: `2px solid ${D.bee}`,
        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
      }}>
        <Zap size={20} color={D.bee} fill={D.bee} />
        <span style={{ fontSize: 14, fontWeight: 800, color: D.beeDark }}>Earn XP • Level Up • Win Rewards</span>
      </div>
    </div>,

    <div key={1} style={{ textAlign: "center", padding: "40px 28px" }}>
      <Mascot size={80} expression="thinking" />
      <div style={{ fontSize: 24, fontWeight: 900, color: D.whale, marginTop: 16 }}>How it works</div>
      <div style={{ marginTop: 20, textAlign: "left" }}>
        {[
          { n: "1", text: "Phone records during your shift", sub: "Sits in your apron pocket", xp: "+40 XP", emoji: "🎙️" },
          { n: "2", text: "AI scores your upselling", sub: "Drink pairings, add-ons, specials", emoji: "🧠" },
          { n: "3", text: "Get a scorecard + tips", sub: "See exactly what earns more $$$", xp: "+25 XP", emoji: "📊" },
        ].map((s, i) => (
          <div key={i} style={{
            display: "flex", gap: 12, marginBottom: 14, alignItems: "center",
            background: D.polar, borderRadius: 14, padding: 14,
            border: `2px solid ${D.arctic}`,
          }}>
            <div style={{ fontSize: 28 }}>{s.emoji}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: D.whale }}>{s.text}</div>
              <div style={{ fontSize: 12, color: D.hare }}>{s.sub}</div>
            </div>
            {s.xp && (
              <div style={{ background: "#FFF8E1", borderRadius: 8, padding: "3px 8px" }}>
                <span style={{ fontSize: 10, fontWeight: 800, color: D.bee }}>{s.xp}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>,

    // NEW: Gifting + Referrals onboarding step
    <div key={2} style={{ textAlign: "center", padding: "30px 28px" }}>
      <Mascot size={80} expression="gift" />
      <div style={{ fontSize: 24, fontWeight: 900, color: D.whale, marginTop: 16 }}>Better Together 🎁</div>
      <div style={{ fontSize: 14, color: D.hare, marginTop: 8, lineHeight: 1.6 }}>
        Earn rewards and <span style={{ color: D.lily, fontWeight: 800 }}>gift them to friends</span>. The more you share, the more you earn!
      </div>

      <div style={{ marginTop: 16, textAlign: "left" }}>
        {[
          { emoji: "🎁", title: "Gift XP & Rewards", desc: "Send gems, free coffee, or streak shields to crewmates", color: D.lily },
          { emoji: "🔄", title: "The Gift Loop", desc: "Friends gift back — top gifters earn 2x bonus XP", color: D.macaw },
          { emoji: "🤝", title: "Refer Friends", desc: "Invite servers you know — you both earn big rewards", color: D.feather },
        ].map((item, i) => (
          <div key={i} style={{
            display: "flex", gap: 12, marginBottom: 10, alignItems: "center",
            background: `${item.color}10`, borderRadius: 14, padding: 14,
            border: `2px solid ${item.color}30`,
          }}>
            <div style={{ fontSize: 28 }}>{item.emoji}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: D.whale }}>{item.title}</div>
              <div style={{ fontSize: 12, color: D.hare }}>{item.desc}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{
        marginTop: 12, background: D.whale, borderRadius: 14, padding: "12px 16px",
      }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: D.hare, textTransform: "uppercase", marginBottom: 4 }}>Referral bonuses</div>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1, background: "rgba(255,255,255,0.08)", borderRadius: 10, padding: "8px 6px", textAlign: "center" }}>
            <div style={{ fontSize: 10, color: D.hare }}>Same restaurant</div>
            <div style={{ fontSize: 15, fontWeight: 900, color: D.feather }}>+100 XP</div>
            <div style={{ fontSize: 9, color: D.hare }}>+ 50 💎 each</div>
          </div>
          <div style={{ flex: 1, background: "rgba(255,255,255,0.08)", borderRadius: 10, padding: "8px 6px", textAlign: "center" }}>
            <div style={{ fontSize: 10, color: D.hare }}>Other restaurant</div>
            <div style={{ fontSize: 15, fontWeight: 900, color: D.lily }}>+250 XP</div>
            <div style={{ fontSize: 9, color: D.hare }}>+ 100 💎 + 🏅</div>
          </div>
        </div>
      </div>
    </div>,

    <div key={3} style={{ textAlign: "center", padding: "40px 28px" }}>
      <div style={{
        width: 80, height: 80, borderRadius: 40,
        background: "#E8F5E9", display: "flex", alignItems: "center", justifyContent: "center",
        margin: "0 auto", border: `3px solid ${D.feather}`,
      }}>
        <Shield size={40} color={D.feather} />
      </div>
      <div style={{ fontSize: 24, fontWeight: 900, color: D.whale, marginTop: 16 }}>Your audio is yours.</div>
      <div style={{ fontSize: 14, color: D.hare, marginTop: 10, lineHeight: 1.6 }}>
        Management <span style={{ color: D.cardinal, fontWeight: 800 }}>NEVER</span> hears your recordings. Audio is processed, scored, then <span style={{ color: D.feather, fontWeight: 800 }}>deleted</span>.
      </div>

      <div style={{
        marginTop: 18, background: D.polar, borderRadius: 14, padding: 16,
        border: `2px solid ${D.arctic}`, textAlign: "left",
      }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: D.whale, marginBottom: 10 }}>Management sees:</div>
        {[
          { text: "Revenue per server", ok: true },
          { text: "Upsell % and team trends", ok: true },
          { text: "Audio recordings", ok: false },
          { text: "Transcripts", ok: false },
          { text: "The Crew feed or gifts", ok: false },
        ].map((item, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <div style={{
              width: 24, height: 24, borderRadius: 12,
              background: item.ok ? D.feather : D.cardinal,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {item.ok ? <Check size={14} color={D.snow} /> : <X size={14} color={D.snow} />}
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color: item.ok ? D.eel : D.cardinal }}>{item.text}</span>
          </div>
        ))}
      </div>
    </div>,

    <div key={4} style={{ textAlign: "center", padding: "40px 28px" }}>
      <Mascot size={90} expression="fire" />
      <div style={{ fontSize: 24, fontWeight: 900, color: D.whale, marginTop: 16 }}>Let's practice!</div>
      <div style={{ fontSize: 14, color: D.hare, marginTop: 8 }}>Do a quick drill and earn your first XP.</div>

      <div style={{
        marginTop: 20, background: D.polar, borderRadius: 16, padding: 20,
        border: `2px solid ${D.arctic}`, borderBottom: `4px solid ${D.arctic}`,
      }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>🎙️</div>
        <div style={{ fontSize: 15, fontWeight: 800, color: D.whale }}>60-Sec Drill</div>
        <div style={{ fontSize: 12, color: D.hare, marginTop: 6, lineHeight: 1.5 }}>
          "A guest asked what's good. Pitch the Grove Burger with an add-on and a drink."
        </div>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 6, marginTop: 12,
          background: "#FFF8E1", borderRadius: 10, padding: "8px 14px",
          border: `2px solid ${D.bee}`,
        }}>
          <Zap size={16} color={D.bee} fill={D.bee} />
          <span style={{ fontSize: 13, fontWeight: 800, color: D.beeDark }}>+25 XP</span>
          <span style={{ fontSize: 11, color: D.hare }}>• 80%+ = +15 bonus</span>
        </div>
      </div>
    </div>,
  ];

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", background: D.snow }}>
      <div style={{ padding: "8px 20px", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={onDone} style={{
          background: "none", border: "none", color: D.hare, fontSize: 18, cursor: "pointer", padding: 4,
        }}>✕</button>
        <div style={{ flex: 1, background: D.arctic, borderRadius: 6, height: 10, overflow: "hidden" }}>
          <div style={{
            width: `${((step + 1) / steps.length) * 100}%`,
            height: "100%", background: D.feather, borderRadius: 6, transition: "width 0.3s",
          }} />
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto" }}>
        {steps[step]}
      </div>

      <div style={{ padding: "0 20px 20px" }}>
        <DuoButton onClick={() => {
          if (step < steps.length - 1) setStep(step + 1);
          else onDone();
        }}>
          {step === steps.length - 1 ? "Let's Go!" : "Continue"}
        </DuoButton>
        {step < steps.length - 1 && (
          <DuoOutlineButton onClick={onDone} style={{ marginTop: 10 }}>
            Skip
          </DuoOutlineButton>
        )}
      </div>
    </div>
  );
}

// ============================================
// MAIN APP
// ============================================
export default function BespokeApp() {
  const [screen, setScreen] = useState("learn");
  const [showOnboarding, setShowOnboarding] = useState(true);
  const [showDrill, setShowDrill] = useState(false);
  const [showGift, setShowGift] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [showShift, setShowShift] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (isRecording) {
      intervalRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } else { clearInterval(intervalRef.current); }
    return () => clearInterval(intervalRef.current);
  }, [isRecording]);

  const handleToggleRecord = () => {
    if (!isRecording && seconds === 0) { setIsRecording(true); setShowShift(true); }
    else if (isRecording) { setIsRecording(false); }
    else { setIsRecording(true); }
  };
  const handleEndShift = () => { setIsRecording(false); setSeconds(999); setShowShift(false); };
  const handleOpenGift = () => setShowGift(true);
  const handleCloseGift = () => setShowGift(false);

  const renderScreen = () => {
    if (showOnboarding) return <OnboardingScreen onDone={() => setShowOnboarding(false)} />;
    if (showDrill) return <DrillScreen onBack={() => setShowDrill(false)} onOpenGift={handleOpenGift} />;
    if (showShift || (screen === "shift")) return <ShiftScreen isRecording={isRecording} seconds={seconds} onEnd={handleEndShift} onOpenGift={handleOpenGift} />;
    switch (screen) {
      case "learn": return <LearnScreen onStartDrill={() => setShowDrill(true)} />;
      case "earnings": return <EarningsScreen isRecording={isRecording} />;
      case "crew": return <CrewScreen isRecording={isRecording} onOpenGift={handleOpenGift} />;
      case "profile": return <ProfileScreen isRecording={isRecording} />;
      default: return <LearnScreen onStartDrill={() => setShowDrill(true)} />;
    }
  };

  const showTabBar = !showOnboarding && !showDrill && !showGift;

  return (
    <div style={{
      minHeight: "100vh", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      background: "#1a1a1a", padding: 20, gap: 16,
    }}>
      <StyleInjector />
      <div style={{ textAlign: "center", fontFamily: "-apple-system, sans-serif" }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#58CC02", letterSpacing: 2, textTransform: "uppercase" }}>
          Bespoke × The Grove SF
        </div>
        <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
          Duolingo Edition v2 • Social Gifting + Referrals • March 2026
        </div>
      </div>

      <IPhoneFrame time={isRecording ? "12:47" : "9:41"}>
        <div style={{ height: "100%", position: "relative" }}>
          {renderScreen()}
          {showGift && <GiftModal onClose={handleCloseGift} />}
          {showTabBar && (
            <TabBar
              active={showShift ? "shift" : screen}
              onNav={(id) => { setShowShift(false); setScreen(id); }}
              isRecording={isRecording}
              onToggleRecord={handleToggleRecord}
            />
          )}
        </div>
      </IPhoneFrame>

      <div style={{
        display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center",
        fontFamily: "-apple-system, sans-serif",
      }}>
        {[
          { id: "onboarding", label: "📖 Onboard" },
          { id: "learn", label: "🎓 Learn" },
          { id: "earnings", label: "💰 Earn" },
          { id: "shift", label: "🎙️ Shift" },
          { id: "crew", label: "👥 Crew" },
          { id: "profile", label: "🐸 Profile" },
          { id: "gift", label: "🎁 Gift" },
        ].map((s) => (
          <button key={s.id} onClick={() => {
            if (s.id === "gift") { handleOpenGift(); return; }
            setShowGift(false);
            if (s.id === "onboarding") { setShowOnboarding(true); setShowDrill(false); setShowShift(false); }
            else if (s.id === "shift") { setShowShift(true); setShowOnboarding(false); setShowDrill(false); }
            else { setShowOnboarding(false); setShowDrill(false); setShowShift(false); setScreen(s.id); }
          }} style={{
            padding: "8px 14px", borderRadius: 10,
            background: (s.id === "gift" && showGift) || (s.id === "onboarding" && showOnboarding) || (s.id === "shift" && showShift) || (!showOnboarding && !showShift && !showGift && screen === s.id)
              ? "#58CC02" : "#222",
            border: "2px solid #333",
            borderBottom: "3px solid #333",
            color: (s.id === "gift" && showGift) || (s.id === "onboarding" && showOnboarding) || (s.id === "shift" && showShift) || (!showOnboarding && !showShift && !showGift && screen === s.id)
              ? "#FFF" : "#888",
            fontSize: 12, fontWeight: 800, cursor: "pointer", textTransform: "uppercase",
          }}>
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}
